/**
 * Landing Page Component
 * Displays introduction and allows content upload
 */
import React, { useState, useEffect } from 'react';
import APIService from '../services/APIService';

const LandingPage = ({ onContentLoaded, onNavigateTo }) => {
  const [loading] = useState(false);
  const [error, setError] = useState(null);
  const [apiHealthy, setApiHealthy] = useState(true);

  useEffect(() => {
    // Check API health
    const checkHealth = async () => {
      try {
        const healthy = await APIService.healthCheck();
        setApiHealthy(healthy);
        if (!healthy) {
          setError('Backend API is not responding. Make sure the server is running on http://localhost:8000');
        }
      } catch {
        setApiHealthy(false);
        setError('Unable to connect to backend API');
      }
    };

    checkHealth();
  }, []);

  const handleLoadSampleData = () => {
    onContentLoaded();
    onNavigateTo('topic-selector');
  };

  return (
    <div className="landing-container">
      <div className="landing-header">
        <h1 className="landing-title">Master Your Knowledge</h1>
        <p className="landing-subtitle">
          AI-powered learning platform with intelligent feedback, voice input, and performance analytics
        </p>
      </div>

      {!apiHealthy && (
        <div style={{
          padding: 'var(--space-4)',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '2px solid var(--danger)',
          borderRadius: 'var(--radius-lg)',
          color: 'var(--danger)',
          marginBottom: 'var(--space-6)',
          fontSize: 'var(--text-base)',
          fontWeight: 'var(--font-weight-600)',
        }}>
          ⚠️ Backend API is not responding. Make sure the FastAPI server is running on http://localhost:8000
        </div>
      )}

      {error && (
        <div style={{
          padding: 'var(--space-4)',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: 'var(--radius-lg)',
          color: 'var(--danger)',
          marginBottom: 'var(--space-6)',
          fontSize: 'var(--text-sm)',
        }}>
          {error}
        </div>
      )}

      {/* Features Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: 'var(--space-6)',
        marginBottom: 'var(--space-12)',
      }}>
        <div style={{
          padding: 'var(--space-8)',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          textAlign: 'center',
          transition: 'all var(--transition-normal)',
          cursor: 'default',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = 'var(--primary)';
          e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
          e.currentTarget.style.transform = 'translateY(-4px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'var(--border)';
          e.currentTarget.style.boxShadow = 'var(--shadow-md)';
          e.currentTarget.style.transform = 'translateY(0)';
        }}>
          <div style={{ fontSize: '2.5rem', marginBottom: 'var(--space-4)' }}>📚</div>
          <h3 style={{
            fontSize: 'var(--text-lg)',
            fontWeight: 'var(--font-weight-700)',
            color: 'var(--text-primary)',
            marginBottom: 'var(--space-2)',
          }}>
            Interactive Learning
          </h3>
          <p style={{
            fontSize: 'var(--text-sm)',
            color: 'var(--text-secondary)',
            lineHeight: '1.6',
          }}>
            Engage with flashcards across multiple topics
          </p>
        </div>

        <div style={{
          padding: 'var(--space-8)',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          textAlign: 'center',
          transition: 'all var(--transition-normal)',
          cursor: 'default',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = 'var(--primary)';
          e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
          e.currentTarget.style.transform = 'translateY(-4px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'var(--border)';
          e.currentTarget.style.boxShadow = 'var(--shadow-md)';
          e.currentTarget.style.transform = 'translateY(0)';
        }}>
          <div style={{ fontSize: '2.5rem', marginBottom: 'var(--space-4)' }}>🎤</div>
          <h3 style={{
            fontSize: 'var(--text-lg)',
            fontWeight: 'var(--font-weight-700)',
            color: 'var(--text-primary)',
            marginBottom: 'var(--space-2)',
          }}>
            Voice & Text Input
          </h3>
          <p style={{
            fontSize: 'var(--text-sm)',
            color: 'var(--text-secondary)',
            lineHeight: '1.6',
          }}>
            Answer questions your way - text or voice
          </p>
        </div>

        <div style={{
          padding: 'var(--space-8)',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          textAlign: 'center',
          transition: 'all var(--transition-normal)',
          cursor: 'default',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = 'var(--primary)';
          e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
          e.currentTarget.style.transform = 'translateY(-4px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'var(--border)';
          e.currentTarget.style.boxShadow = 'var(--shadow-md)';
          e.currentTarget.style.transform = 'translateY(0)';
        }}>
          <div style={{ fontSize: '2.5rem', marginBottom: 'var(--space-4)' }}>🤖</div>
          <h3 style={{
            fontSize: 'var(--text-lg)',
            fontWeight: 'var(--font-weight-700)',
            color: 'var(--text-primary)',
            marginBottom: 'var(--space-2)',
          }}>
            AI Evaluation
          </h3>
          <p style={{
            fontSize: 'var(--text-sm)',
            color: 'var(--text-secondary)',
            lineHeight: '1.6',
          }}>
            Get instant feedback from AI
          </p>
        </div>

        <div style={{
          padding: 'var(--space-8)',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          textAlign: 'center',
          transition: 'all var(--transition-normal)',
          cursor: 'default',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = 'var(--primary)';
          e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
          e.currentTarget.style.transform = 'translateY(-4px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'var(--border)';
          e.currentTarget.style.boxShadow = 'var(--shadow-md)';
          e.currentTarget.style.transform = 'translateY(0)';
        }}>
          <div style={{ fontSize: '2.5rem', marginBottom: 'var(--space-4)' }}>📊</div>
          <h3 style={{
            fontSize: 'var(--text-lg)',
            fontWeight: 'var(--font-weight-700)',
            color: 'var(--text-primary)',
            marginBottom: 'var(--space-2)',
          }}>
            Performance Reports
          </h3>
          <p style={{
            fontSize: 'var(--text-sm)',
            color: 'var(--text-secondary)',
            lineHeight: '1.6',
          }}>
            Track progress with analytics
          </p>
        </div>
      </div>

      {/* CTA Button */}
      <div style={{ textAlign: 'center', marginBottom: 'var(--space-12)' }}>
        <button
          onClick={handleLoadSampleData}
          disabled={loading || !apiHealthy}
          className="start-session-btn"
          style={{ fontSize: 'var(--text-lg)' }}
        >
          {loading ? '⏳ Loading...' : '🚀 Get Started Now'}
        </button>
      </div>

      {/* How It Works */}
      <div style={{
        padding: 'var(--space-8)',
        background: 'linear-gradient(135deg, rgba(96, 165, 250, 0.05) 0%, rgba(6, 182, 212, 0.05) 100%)',
        border: '1px solid rgba(96, 165, 250, 0.2)',
        borderRadius: 'var(--radius-lg)',
        maxWidth: '600px',
        margin: '0 auto',
      }}>
        <h3 style={{
          fontSize: 'var(--text-lg)',
          fontWeight: 'var(--font-weight-700)',
          color: 'var(--text-primary)',
          marginBottom: 'var(--space-6)',
        }}>
          How It Works
        </h3>
        <ol style={{
          listStyle: 'none',
          padding: 0,
          display: 'grid',
          gap: 'var(--space-4)',
        }}>
          <li style={{
            display: 'flex',
            gap: 'var(--space-4)',
            alignItems: 'flex-start',
            fontSize: 'var(--text-sm)',
            color: 'var(--text-secondary)',
          }}>
            <span style={{
              fontWeight: 'var(--font-weight-700)',
              color: 'var(--primary)',
              minWidth: '24px',
            }}>
              1️⃣
            </span>
            <span>Select your topics of interest</span>
          </li>
          <li style={{
            display: 'flex',
            gap: 'var(--space-4)',
            alignItems: 'flex-start',
            fontSize: 'var(--text-sm)',
            color: 'var(--text-secondary)',
          }}>
            <span style={{
              fontWeight: 'var(--font-weight-700)',
              color: 'var(--primary)',
              minWidth: '24px',
            }}>
              2️⃣
            </span>
            <span>Start a learning session with your preferred mode</span>
          </li>
          <li style={{
            display: 'flex',
            gap: 'var(--space-4)',
            alignItems: 'flex-start',
            fontSize: 'var(--text-sm)',
            color: 'var(--text-secondary)',
          }}>
            <span style={{
              fontWeight: 'var(--font-weight-700)',
              color: 'var(--primary)',
              minWidth: '24px',
            }}>
              3️⃣
            </span>
            <span>Answer questions with text or voice input</span>
          </li>
          <li style={{
            display: 'flex',
            gap: 'var(--space-4)',
            alignItems: 'flex-start',
            fontSize: 'var(--text-sm)',
            color: 'var(--text-secondary)',
          }}>
            <span style={{
              fontWeight: 'var(--font-weight-700)',
              color: 'var(--primary)',
              minWidth: '24px',
            }}>
              4️⃣
            </span>
            <span>Get instant AI-powered feedback</span>
          </li>
          <li style={{
            display: 'flex',
            gap: 'var(--space-4)',
            alignItems: 'flex-start',
            fontSize: 'var(--text-sm)',
            color: 'var(--text-secondary)',
          }}>
            <span style={{
              fontWeight: 'var(--font-weight-700)',
              color: 'var(--primary)',
              minWidth: '24px',
            }}>
              5️⃣
            </span>
            <span>Review detailed performance report and recommendations</span>
          </li>
        </ol>
      </div>
    </div>
  );
};

export default LandingPage;
